// Backend API Configuration
// External API for AZG Reserve Data (hosted on Vercel)
const AZG_API_ENDPOINT = process.env.REACT_APP_AZG_RESERVE_API || 'https://arizore-reserve-backend.vercel.app/api/azg-reserve';
const AZS_API_ENDPOINT = process.env.REACT_APP_AZS_RESERVE_API || 'https://arizore-reserve-backend.vercel.app/api/azs-reserve';

/**
 * Fetch AZG Reserve Data from Backend
 * Returns: {
 *   totalGoldReserves: number (oz) - formatted to 2 decimals,
 *   totalGoldIG: number (oz) - In-Ground gold,
 *   totalGoldT: number (oz) - Tailings gold,
 *   totalGoldBullion: number (oz) - Vaulted bullion gold,
 *   compositionIG: number (percentage) - formatted to 2 decimals,
 *   compositionTailings: number (percentage) - formatted to 2 decimals,
 *   compositionBullion: number (percentage) - formatted to 2 decimals,
 *   totalTokensInCirculation: number - Total AZG Tokens in Circulation,
 *   currentTokenValue: number - AZG Current Token Value,
 *   lastUpdated: string (ISO date)
 * }
 */
export const fetchAZGReserveData = async () => {
  try {
    const response = await fetch(AZG_API_ENDPOINT, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Backend returns:
    // {
    //   "totalGoldReservesOz": 39186.22,
    //   "totalGoldIG": number,
    //   "totalGoldT": number,
    //   "totalGoldBullion": number,
    //   "azgComposition": {
    //     "inGround": 0.5406549,
    //     "tailings": 0.4593453,
    //     "bullion": 0
    //   },
    //   "totalAZGTokensInCirculation": number,
    //   "azgCurrentTokenValue": number,
    //   "lastUpdatedIso": null
    // }
    
    return {
      totalGoldReserves: parseFloat((data.totalGoldReservesOz || 0).toFixed(2)),
      totalGoldIG: parseFloat((data.totalGoldIG || 0).toFixed(2)),
      totalGoldT: parseFloat((data.totalGoldT || 0).toFixed(2)),
      totalGoldBullion: parseFloat((data.totalGoldBullion || 0).toFixed(2)),
      compositionIG: parseFloat(((data.azgComposition?.inGround || 0) * 100).toFixed(2)),
      compositionTailings: parseFloat(((data.azgComposition?.tailings || 0) * 100).toFixed(2)),
      compositionBullion: parseFloat(((data.azgComposition?.bullion || 0) * 100).toFixed(2)),
      totalTokensInCirculation: data.totalAZGTokensInCirculation || 0,
      currentTokenValue: parseFloat((data.azgCurrentTokenValue || 0).toFixed(2)),
      reserveValue: data.azgReserveValue != null ? parseFloat(data.azgReserveValue.toFixed(2)) : null,
      totalReserveValue: typeof data.azgTotalReserveValue === 'number' ? parseFloat(data.azgTotalReserveValue.toFixed(2)) : null,
      lastUpdated: data.lastUpdatedIso || new Date().toISOString()
    };
  } catch (error) {
    console.error('Error fetching AZG Reserve Data from backend:', error);
    throw error;
  }
};

/**
 * Fetch AZS Reserve Data from Backend
 * Returns: {
 *   totalSilverReserves: number (oz) - Total Silver Reserves,
 *   totalSilverIG: number (oz) - In-Ground silver,
 *   totalSilverT: number (oz) - Tailings silver,
 *   totalSilverBullion: number (oz) - Vaulted bullion silver,
 *   compositionIG: number (percentage) - AZS Reserve Composition_IG,
 *   compositionTailings: number (percentage) - AZS Reserve Composition_Tailings,
 *   compositionBullion: number (percentage) - AZS Reserve Composition_Bullion,
 *   totalTokensInCirculation: number - Total AZS Tokens in Circulation,
 *   currentTokenValue: number - AZS Current Token Value,
 *   lastUpdated: string (ISO date)
 * }
 */
export const fetchAZSReserveData = async () => {
  try {
    const response = await fetch(AZS_API_ENDPOINT, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Backend returns:
    // {
    //   "totalSilverReservesOz": number,
    //   "totalSilverIG": number,
    //   "totalSilverT": number,
    //   "totalSilverBullion": number,
    //   "azsComposition": {
    //     "inGround": number,
    //     "tailings": number,
    //     "bullion": number
    //   },
    //   "totalAZSTokensInCirculation": number,
    //   "azsCurrentTokenValue": number,
    //   "lastUpdatedIso": string
    // }
    
    return {
      totalSilverReserves: parseFloat((data.totalSilverReservesOz || 0).toFixed(2)),
      totalSilverIG: parseFloat((data.totalSilverIG || 0).toFixed(2)),
      totalSilverT: parseFloat((data.totalSilverT || 0).toFixed(2)),
      totalSilverBullion: parseFloat((data.totalSilverBullion || 0).toFixed(2)),
      compositionIG: parseFloat(((data.azsComposition?.inGround || 0) * 100).toFixed(2)),
      compositionTailings: parseFloat(((data.azsComposition?.tailings || 0) * 100).toFixed(2)),
      compositionBullion: parseFloat(((data.azsComposition?.bullion || 0) * 100).toFixed(2)),
      totalTokensInCirculation: data.totalAZSTokensInCirculation || 0,
      currentTokenValue: parseFloat((data.azsCurrentTokenValue || 0).toFixed(2)),
      reserveValue: data.azsReserveValue != null ? parseFloat(data.azsReserveValue.toFixed(2)) : null,
      totalReserveValue: typeof data.azsTotalReserveValue === 'number' ? parseFloat(data.azsTotalReserveValue.toFixed(2)) : null,
      lastUpdated: data.lastUpdatedIso || new Date().toISOString()
    };
  } catch (error) {
    console.error('Error fetching AZS Reserve Data from backend:', error);
    throw error;
  }
};
